import type { AutomationNeedingAttentionResolveBody } from 'app/pages/HumanInTheLoopPage/apiService/humanInTheLoopApiService';

export const NURTURING_AUTOMATION_EVENT_PREFIX = 'nurturing_test_automation_';
export const NURTURING_AUTOMATION_EVENT_COMPLETED =
  'nurturing_test_automation_completed';
export const NURTURING_AUTOMATION_EVENT_REQUEST_RECEIVED =
  'nurturing_test_automation_request_received';
export const NURTURING_AUTOMATION_EVENT_DEALER_PROGRESS =
  'nurturing_test_automation_dealer_progress';
export const NURTURING_AUTOMATION_EVENT_LEAD_PROGRESS =
  'nurturing_test_automation_lead_progress';

export type TestNurturingJourneyResult = 'success' | 'failure';

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Returns "success" when sent count matches expected count, else "failure".
 */
export function getTestNurturingJourneyResultStatus(
  messagesSentCount: unknown,
  expectedSentCount: unknown,
): TestNurturingJourneyResult {
  const sent = toFiniteNumber(messagesSentCount);
  const expected = toFiniteNumber(expectedSentCount);
  if (sent == null || expected == null) return 'failure';
  return sent === expected ? 'success' : 'failure';
}

export type NurturingAutomationSocketMessage = {
  eventName: string;
  data: unknown;
  timestamp: number;
};

export function isNurturingAutomationHttpSuccess(statusCode?: number): boolean {
  if (statusCode == null) return true;
  return statusCode >= 200 && statusCode < 300;
}

export function getNurturingAutomationSocketMessage(
  payload: unknown,
): Record<string, unknown> {
  if (!payload || typeof payload !== 'object') return {};
  const p = payload as Record<string, unknown>;
  const nested = p.message;
  if (nested && typeof nested === 'object') {
    return nested as Record<string, unknown>;
  }
  return p;
}

export function getNurturingAutomationJobId(payload: unknown): string {
  const msg = getNurturingAutomationSocketMessage(payload);
  let value = msg.job_id ?? msg.jobId;
  if (value == null && payload && typeof payload === 'object') {
    const root = payload as Record<string, unknown>;
    value = root.job_id ?? root.jobId;
  }
  if (value == null) return '';
  return String(value).trim();
}

export function getNurturingAutomationLeadCounts(payload: unknown): {
  current: number | null;
  total: number | null;
} {
  const msg = getNurturingAutomationSocketMessage(payload);
  const totalRaw = msg.total_leads ?? msg.total ?? msg.lead_count;
  const currentRaw = msg.completed_leads ?? msg.current;
  const total =
    typeof totalRaw === 'number' && Number.isFinite(totalRaw)
      ? Math.max(0, totalRaw)
      : null;
  const current =
    typeof currentRaw === 'number' && Number.isFinite(currentRaw)
      ? Math.max(0, currentRaw)
      : null;
  return { current, total };
}

export function extractJobIdsFromResolveBody(
  body: AutomationNeedingAttentionResolveBody | null | undefined,
): string[] {
  if (!body || typeof body !== 'object') return [];
  const ids = new Set<string>();

  const reruns = body.reruns;
  if (Array.isArray(reruns)) {
    for (const rerun of reruns) {
      const testId = rerun?.test_automation_id?.trim();
      if (testId) ids.add(testId);
      const jobTopic = rerun?.websocket?.subscribe_job_topic?.trim();
      if (jobTopic) ids.add(jobTopic);
    }
  }

  const topJobTopic = body.websocket?.subscribe_job_topic?.trim();
  if (topJobTopic) ids.add(topJobTopic);

  return Array.from(ids);
}

export function getResolveProgressTotal(
  body: AutomationNeedingAttentionResolveBody | null | undefined,
  fallbackTotal: number,
): number {
  if (!body || typeof body !== 'object') {
    return Math.max(1, fallbackTotal);
  }

  let sum = 0;
  const reruns = body.reruns;
  if (Array.isArray(reruns)) {
    for (const rerun of reruns) {
      if (Array.isArray(rerun.items) && rerun.items.length > 0) {
        sum += rerun.items.length;
      } else if (Array.isArray(rerun.task_ids) && rerun.task_ids.length > 0) {
        sum += rerun.task_ids.length;
      }
    }
  }

  if (sum > 0) return Math.max(1, sum);

  const rescheduled = body.rescheduled_count;
  if (typeof rescheduled === 'number' && Number.isFinite(rescheduled) && rescheduled > 0) {
    return Math.max(1, rescheduled);
  }

  return Math.max(1, fallbackTotal);
}

export function getNurturingAutomationStatusText(payload: unknown): string {
  const msg = getNurturingAutomationSocketMessage(payload);
  const statusText = msg.status_text ?? msg.statusText;
  if (typeof statusText === 'string' && statusText.trim()) {
    return statusText.trim();
  }
  const nestedMessage = msg.message;
  if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
    return nestedMessage.trim();
  }
  return '';
}

function formatReasonCodeLabel(reasonCode: unknown): string {
  if (typeof reasonCode !== 'string' || !reasonCode.trim()) return '';
  return reasonCode
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

export function formatAttentionRetryAllSocketLine(
  eventName: string,
  payload: unknown,
): string {
  const statusText = getNurturingAutomationStatusText(payload);
  if (statusText) return statusText;

  const msg = getNurturingAutomationSocketMessage(payload);
  const segmentName =
    typeof msg.segment_name === 'string' ? msg.segment_name.trim() : '';
  const reasonCode = formatReasonCodeLabel(msg.reason_code);
  const segmentIndex =
    typeof msg.segment_index === 'number' ? msg.segment_index : null;
  const segmentTotal =
    typeof msg.segment_total === 'number' ? msg.segment_total : null;
  const taskIndex = typeof msg.task_index === 'number' ? msg.task_index : null;
  const taskTotal = typeof msg.task_total === 'number' ? msg.task_total : null;
  const phase = typeof msg.phase === 'string' ? msg.phase : '';
  const error = typeof msg.error === 'string' ? msg.error.trim() : '';

  if (segmentName && taskIndex != null && taskTotal != null) {
    return `${segmentName}: task ${taskIndex}/${taskTotal}${error ? ` — ${error}` : ''}`;
  }
  if (segmentName && segmentIndex != null && segmentTotal != null) {
    const reasonSuffix = reasonCode ? ` (${reasonCode})` : '';
    return `Segment ${segmentIndex}/${segmentTotal}: ${segmentName}${reasonSuffix}${error ? ` — ${error}` : ''}`;
  }
  if (phase) {
    return phase.replace(/_/g, ' ');
  }

  return eventName.replace(/^nurturing_test_automation_/, '').replace(/_/g, ' ');
}

export function getAttentionRetryAllStepLabel(
  eventName: string,
  payload?: unknown,
): string {
  const msg = payload ? getNurturingAutomationSocketMessage(payload) : {};
  const step = typeof msg.step === 'string' ? msg.step.trim() : '';
  if (step) {
    return step.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  return eventName
    .replace(/^nurturing_test_automation_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export type AttentionRetryAllProgressContext = {
  /** Last known segment index from prior socket events. */
  segmentIndex?: number;
  /** Last known segment total from prior socket events. */
  segmentTotal?: number;
  /** Session fallback when socket omits totals. */
  progressTotalFallback?: number;
  /** Monotonic floor — progress never visually moves backward. */
  lastPercentage?: number;
};

/** Coarse UI tiers — only batch + segment-boundary events update the progress card. */
export type AttentionRetryAllUiTier = 'batch' | 'segment' | 'detail';

const ATTENTION_RETRY_ALL_BATCH_STEPS = new Set([
  'loading_segments',
  'segments_loaded',
  'batch_started',
  'batch_completed',
  'test_rerun_phase_started',
]);

const ATTENTION_RETRY_ALL_SEGMENT_BOUNDARY_STEPS = new Set([
  'segment_started',
  'segment_completed',
  'segment_failed',
]);

export function getAttentionRetryAllUiTier(
  step: string,
  eventName: string,
): AttentionRetryAllUiTier {
  // Step wins over event name — coarse steps are broadcast on lead_progress too.
  if (ATTENTION_RETRY_ALL_BATCH_STEPS.has(step)) {
    return 'batch';
  }
  if (ATTENTION_RETRY_ALL_SEGMENT_BOUNDARY_STEPS.has(step)) {
    return 'segment';
  }
  if (
    step.startsWith('task_') ||
    step.startsWith('test_rerun_') ||
    step === 'segment_finding_tasks' ||
    step === 'segment_tasks_found' ||
    step === 'segment_updating_retry_dates'
  ) {
    return 'detail';
  }
  if (step.startsWith('segment_')) {
    return 'segment';
  }

  if (
    eventName === NURTURING_AUTOMATION_EVENT_COMPLETED ||
    eventName === NURTURING_AUTOMATION_EVENT_REQUEST_RECEIVED
  ) {
    return 'batch';
  }
  if (
    eventName === NURTURING_AUTOMATION_EVENT_DEALER_PROGRESS ||
    eventName === NURTURING_AUTOMATION_EVENT_LEAD_PROGRESS
  ) {
    return 'detail';
  }
  return 'detail';
}

/** True when the progress card / dialog should refresh for this socket payload. */
export function shouldUpdateAttentionRetryAllUi(
  step: string,
  eventName: string,
  isCompletion: boolean,
): boolean {
  if (isCompletion) return true;
  const tier = getAttentionRetryAllUiTier(step, eventName);
  return tier === 'batch' || tier === 'segment';
}

function getAttentionRetryAllStepWeight(step: string): number | null {
  if (!step) return null;
  const weights: Record<string, number> = {
    loading_segments: 0.04,
    segments_loaded: 0.08,
    batch_started: 0.12,
    segment_started: 0.14,
    segment_retry: 0.16,
    finding_tasks: 0.24,
    tasks_found: 0.3,
    task_reschedule_started: 0.38,
    task_reschedule_before: 0.48,
    task_reschedule_after: 0.82,
    test_rerun_started: 0.52,
    test_rerun_completed: 0.9,
    segment_completed: 1,
    segment_failed: 1,
    batch_completed: 1,
  };
  if (step in weights) return weights[step];
  if (step.startsWith('task_')) return 0.55;
  if (step.startsWith('segment_')) return 0.2;
  return null;
}

function resolveAttentionRetryAllIntraSegmentProgress(
  payload: unknown,
  eventName: string,
  step: string,
): number {
  const msg = getNurturingAutomationSocketMessage(payload);
  const taskIndex =
    typeof msg.task_index === 'number' ? msg.task_index : null;
  const taskTotal =
    typeof msg.task_total === 'number' ? msg.task_total : null;

  if (taskIndex != null && taskTotal != null && taskTotal > 0) {
    const clampedIndex = Math.min(Math.max(taskIndex, 1), taskTotal);
    return Math.min(0.98, (clampedIndex - 0.35) / taskTotal);
  }

  const leadCounts = getNurturingAutomationLeadCounts(payload);
  if (leadCounts.total != null && leadCounts.total > 0 && leadCounts.current != null) {
    const clampedCurrent = Math.min(Math.max(leadCounts.current, 0), leadCounts.total);
    if (clampedCurrent > 0) {
      return Math.min(0.98, clampedCurrent / leadCounts.total);
    }
  }

  const stepWeight = getAttentionRetryAllStepWeight(step);
  if (stepWeight != null) {
    return Math.min(0.98, stepWeight);
  }

  if (eventName === NURTURING_AUTOMATION_EVENT_LEAD_PROGRESS) {
    return 0.35;
  }

  if (eventName === NURTURING_AUTOMATION_EVENT_DEALER_PROGRESS) {
    return 0.2;
  }

  if (eventName === NURTURING_AUTOMATION_EVENT_REQUEST_RECEIVED) {
    return 0.05;
  }

  return 0.1;
}

export function getAttentionRetryAllLiveProgress(
  payload: unknown,
  eventName = '',
  context: AttentionRetryAllProgressContext = {},
  options?: { coarseUi?: boolean },
): {
  statusText: string;
  stepLabel: string;
  completedSegments: number;
  totalSegments: number;
  percentage: number;
  progressUnits: number;
} {
  const coarseUi = options?.coarseUi ?? true;
  const msg = getNurturingAutomationSocketMessage(payload);
  let statusText = getNurturingAutomationStatusText(payload);

  if (
    !statusText &&
    eventName === NURTURING_AUTOMATION_EVENT_REQUEST_RECEIVED &&
    typeof msg.total_leads === 'number' &&
    msg.total_leads > 0
  ) {
    statusText = `Preparing retry for ${msg.total_leads} segment(s)…`;
  }
  if (!statusText) {
    statusText = formatAttentionRetryAllSocketLine(eventName, payload);
  }

  const totalSegments = Math.max(
    1,
    typeof msg.segment_total === 'number' && msg.segment_total > 0
      ? msg.segment_total
      : typeof msg.total_leads === 'number' && msg.total_leads > 0
        ? msg.total_leads
        : context.segmentTotal && context.segmentTotal > 0
          ? context.segmentTotal
          : context.progressTotalFallback && context.progressTotalFallback > 0
            ? context.progressTotalFallback
            : 1,
  );

  const segmentIndex =
    typeof msg.segment_index === 'number' && msg.segment_index > 0
      ? msg.segment_index
      : context.segmentIndex && context.segmentIndex > 0
        ? context.segmentIndex
        : 0;

  const leadCounts = getNurturingAutomationLeadCounts(payload);
  const completedFromSocket =
    typeof msg.completed_leads === 'number'
      ? Math.max(0, msg.completed_leads)
      : leadCounts.current != null
        ? Math.max(0, leadCounts.current)
        : 0;
  const step = typeof msg.step === 'string' ? msg.step : '';
  const isSegmentDone =
    step === 'segment_completed' ||
    step === 'segment_failed' ||
    step === 'batch_completed' ||
    eventName === NURTURING_AUTOMATION_EVENT_COMPLETED;

  let fullyCompletedSegments = completedFromSocket;
  if (segmentIndex > 0) {
    if (isSegmentDone) {
      fullyCompletedSegments = Math.max(fullyCompletedSegments, segmentIndex);
    } else {
      fullyCompletedSegments = Math.max(fullyCompletedSegments, segmentIndex - 1);
    }
  }

  const intraSegmentProgress = coarseUi
    ? 0
    : isSegmentDone
      ? 1
      : resolveAttentionRetryAllIntraSegmentProgress(payload, eventName, step);

  const progressUnits = coarseUi
    ? Math.min(
        totalSegments,
        isSegmentDone && segmentIndex > 0
          ? segmentIndex
          : Math.max(0, segmentIndex > 0 ? segmentIndex - 1 : fullyCompletedSegments),
      )
    : Math.min(
        totalSegments,
        Math.max(0, fullyCompletedSegments + intraSegmentProgress),
      );

  let percentage = coarseUi
    ? Math.min(
        100,
        Math.max(
          0,
          Math.round((progressUnits / totalSegments) * 100),
        ),
      )
    : Math.min(
        100,
        Math.max(0, Math.round((progressUnits / totalSegments) * 100)),
      );

  if (!coarseUi && eventName === NURTURING_AUTOMATION_EVENT_LEAD_PROGRESS) {
    const segmentSlice = 100 / totalSegments;
    const segmentCeiling = Math.min(
      99,
      segmentIndex > 0
        ? (segmentIndex - 1 + 0.98) * segmentSlice
        : segmentSlice * 0.98,
    );
    const nudged = Math.min(
      segmentCeiling,
      (context.lastPercentage ?? 0) + segmentSlice / 30,
    );
    percentage = Math.max(percentage, Math.round(nudged));
  }

  if (context.lastPercentage != null) {
    percentage = Math.max(context.lastPercentage, percentage);
  }

  const stepLabel = coarseUi
    ? segmentIndex > 0
      ? `Segment ${segmentIndex} of ${totalSegments}`
      : getAttentionRetryAllStepLabel(eventName, payload)
    : getAttentionRetryAllStepLabel(eventName, payload);

  return {
    statusText,
    stepLabel,
    completedSegments: Math.min(
      totalSegments,
      isSegmentDone && segmentIndex > 0
        ? segmentIndex
        : Math.floor(fullyCompletedSegments),
    ),
    totalSegments,
    percentage,
    progressUnits,
  };
}

export function extractAttentionRetryAllResultFromCompletionPayload(
  payload: unknown,
): AutomationNeedingAttentionResolveBody | null {
  const msg = getNurturingAutomationSocketMessage(payload);
  const result = msg.attention_retry_all_result;
  if (!result || typeof result !== 'object') return null;
  return result as AutomationNeedingAttentionResolveBody;
}

export function extractRetryAllAcceptedJobId(
  body:
    | AutomationNeedingAttentionResolveBody
    | { job_id?: string; jobId?: string }
    | null
    | undefined,
): string {
  if (!body || typeof body !== 'object') return '';
  const value =
    'job_id' in body
      ? body.job_id
      : 'jobId' in body
        ? body.jobId
        : undefined;
  if (value == null) return '';
  return String(value).trim();
}
